import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { pipes } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Card, CardBody } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Icon, type IconName } from '@/shared/ui/Icon';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Skeleton } from '@/shared/ui/Skeleton';
import { statusLabel } from '@/shared/ui/statusLabels';

import { BuscaNasEsteiras } from './BuscaNasEsteiras';

const icones: Record<string, IconName> = {
  briefcase: 'briefcase',
  coins: 'coins',
  stamp: 'stamp',
  users: 'users',
  layers: 'layers',
  shield: 'shield',
};

/**
 * Esteiras (/kanban do OnPoint): a lista das esteiras da empresa, na ordem, com o
 * icone e a contagem de cartoes abertos de cada uma.
 *
 * A esteira e configuravel, como no OnPoint. A maquina de estados da operacao
 * continua sendo a regra que move a operacao: o quadro organiza o trabalho e passa
 * pela transicao da operacao quando a coluna muda a etapa dela.
 */
export function PipesPage() {
  const { can } = useAuth();
  const podeVer = can('esteiras.esteira.view');

  const consulta = useQuery({
    queryKey: ['pipes'],
    queryFn: ({ signal }) => pipes.list(signal),
    enabled: podeVer,
  });

  if (!podeVer) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Esteiras" />
        <Alert tone="info" title="Esteiras indisponiveis para o seu acesso">
          O seu perfil nao inclui a visualizacao das esteiras.
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Esteiras"
        description="Cada esteira e um quadro em colunas. O numero e o de cartoes abertos."
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as esteiras">
          {consulta.error.message}
        </Alert>
      ) : null}

      {consulta.isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      ) : (consulta.data ?? []).length === 0 ? (
        <EmptyState
          icon="layers"
          title="Nenhuma esteira ativa"
          description="As esteiras da empresa aparecem aqui."
        />
      ) : (
        <>
          <BuscaNasEsteiras esteiras={consulta.data ?? []} />
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(consulta.data ?? []).map((esteira) => (
              <li key={esteira.id}>
                <Link
                  to={`/esteiras/${esteira.id}`}
                  className="block rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
                >
                  <Card className="h-full transition-shadow hover:shadow-(--shadow-sm)">
                    <CardBody className="flex items-center gap-4">
                      <span className="flex size-11 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-700">
                        <Icon name={icones[esteira.icon_name] ?? 'layers'} size={20} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-ink-900">
                          {esteira.name}
                        </p>
                        <p className="mt-0.5 text-2xs text-ink-500">
                          {esteira.operation_stage
                            ? `Cotacoes em ${statusLabel('operacao', esteira.operation_stage).toLowerCase()}`
                            : 'Sem cartao de cotacao nesta etapa das esteiras'}
                        </p>
                      </div>
                      <span
                        className="text-xl font-semibold tabular-nums text-ink-900"
                        aria-label={`${esteira.open_cards} cartoes abertos`}
                      >
                        {esteira.open_cards}
                      </span>
                    </CardBody>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
